import {
  createMemoryRouter,
  IndexRouteObject,
  LoaderFunctionArgs,
  NonIndexRouteObject,
  RouteObject,
} from 'react-router-dom'

import { JourneyRouteProperties } from '@cash-web/shared-journey-util-main'

type MemoryRouterOptions = Parameters<typeof createMemoryRouter>[1]

export type LoaderFunctionArgsWithContext<TContext> = LoaderFunctionArgs & {
  context: TContext
}

type LoaderWithContext<TContext> = (
  args: LoaderFunctionArgsWithContext<TContext>
) => ReturnType<NonNullable<RouteObject['loader']>>

export type RouteObjectWithContext<TContext> = Omit<RouteObject, 'loader' | 'children'> & {
  loader?: LoaderWithContext<TContext>
  children?: RouteObjectWithContext<TContext>[]
}

type IndexRouteWithContext<TContext, TExtension = unknown> = Omit<IndexRouteObject, 'loader'> &
  TExtension & {
    loader?: LoaderWithContext<TContext>
  }

type NonIndexRouteWithContext<TContext, TExtension = unknown> = Omit<NonIndexRouteObject, 'loader' | 'children'> &
  TExtension & {
    loader?: LoaderWithContext<TContext>
    children?: JourneyRouteWithContext<TContext>[]
  }

export type JourneyRouteWithContext<TContext> =
  | IndexRouteWithContext<TContext, JourneyRouteProperties>
  | NonIndexRouteWithContext<TContext, JourneyRouteProperties>

function transformRoutes<TContext>(routes: RouteObjectWithContext<TContext>[], context: TContext): RouteObject[] {
  return routes.map(({ loader, children, index, ...route }) => {
    if (index) {
      return {
        ...route,
        index: true,
        loader: loader ? (args: LoaderFunctionArgs) => loader({ ...args, context }) : undefined,
      }
    }

    return {
      ...route,
      loader: loader ? (args: LoaderFunctionArgs) => loader({ ...args, context }) : undefined,
      children: children ? transformRoutes(children, context) : undefined,
    }
  })
}

export function createMemoryRouterWithContext<TContext>(
  routes: RouteObjectWithContext<TContext>[],
  opts: MemoryRouterOptions,
  context: TContext
) {
  const transformedRoutes = transformRoutes(routes, context)
  return createMemoryRouter(transformedRoutes, opts)
}
